import { Anchor, Badge, Group, Table, Text } from '@mantine/core';
import { Link } from 'react-router';
import { EmptyNote } from '../../components/RecordScreen';
import { formatDate, formatMoney, formatQty } from '../../lib/format';

/**
 * What the crossing adds to one unit from this vendor, and what that figure is made of.
 *
 * The counts are on screen, not merely in the payload. "75c a bottle" worked out from eleven ferry
 * bills and "75c a bottle" worked out from one are the same number and not the same claim, and the
 * owner is the one who has to decide whether to move an order on it.
 */
export interface CrossingCost {
  /**
   * Worked out from carrier bills, or a vendor who brings it over themselves, or not known at all.
   * Never printed — every screen reading this turns it into a sentence.
   */
  basis: 'derived' | 'delivered' | 'unknown';
  perUnitBaseMinor: number;
  chargeCount: number;
  deliveryCount: number;
  unitsReceived: number;
  totalBaseMinor: number;
  /** The earliest delivery the figure looked at, so a screen can say over what period. */
  sinceDate: string;
}

/**
 * One vendor's offer on one stock item, priced per counted unit with the crossing spread over it.
 * The API works this out; nothing here does arithmetic on money.
 */
export interface LandedOffer {
  supplierId: string;
  supplierName: string;
  packQty: number;
  packUnit: string;
  unitPriceMinor: number;
  /** What the whole pack costs. The API sends it; it is NOT unitPriceMinor × packQty. */
  packPriceMinor: number;
  currency: string;
  unitPriceBaseMinor: number;
  freightPerUnitBaseMinor: number;
  landedUnitCostBaseMinor: number;
  crossingCost: CrossingCost;
  leadTimeDays?: number | null;
  lastPurchaseAt?: string | null;
}

/** Whether the figure in "Costs you" is the whole cost, or only the goods half of it. */
export const crossingIsKnown = (offer: LandedOffer): boolean => offer.crossingCost.basis !== 'unknown';

/**
 * What the crossing column says, and — one line down — what it was worked out from.
 *
 * The three answers are three different facts and the column used to give one of them for all
 * three. It printed the amount when it was above zero and "They deliver" otherwise, and zero was
 * what every vendor in the database had, so a mainland grower whose crates cross on the ferry read
 * as a vendor who drives them round himself. That is the reading that costs money: it is the whole
 * reason to compare vendors, printed backwards.
 *
 * A blank is the honest answer to "what did the ferry charge" when no ferry bill has been recorded,
 * and it has to be a blank that says why — the reader is deciding where to spend, and "we do not
 * know yet" is a usable answer where a confident zero is not.
 */
export function Crossing({ offer, currency, unit }: { offer: LandedOffer; currency: string; unit?: string }) {
  const { basis, chargeCount, deliveryCount, sinceDate } = offer.crossingCost;

  if (basis === 'delivered') {
    return (
      <Text size="sm" c="dimmed">
        They deliver
      </Text>
    );
  }

  if (basis === 'unknown') {
    return (
      <>
        <Text size="sm" c="orange.7">
          Not known
        </Text>
        <Text size="xs" c="dimmed">
          No shipping bill recorded since {formatDate(sinceDate)}
        </Text>
      </>
    );
  }

  const bills = chargeCount === 1 ? '1 shipping bill' : `${chargeCount} shipping bills`;
  const deliveries = deliveryCount === 1 ? '1 delivery' : `${deliveryCount} deliveries`;
  return (
    <>
      <Text size="sm" component="span">
        {formatMoney(offer.freightPerUnitBaseMinor, currency)}
        {unit ? ` a ${unit}` : ' each'}
      </Text>
      <Text size="xs" c="dimmed">
        Average of {bills} over {deliveries} since {formatDate(sinceDate)}
      </Text>
    </>
  );
}

/**
 * What the item costs once it is on the shelf — or, when nobody has recorded a crossing for that
 * vendor, what its goods cost with the crossing missing from it.
 *
 * Both screens print this figure and both used to print it flat, which reads as a total whether or
 * not the shipping half of it is in there. Said out loud on the row it is a comparison the reader
 * can still use; left unsaid it is the vendor the ranking recommends looking cheapest by exactly
 * the amount nobody has written down.
 */
export function LandedCost({ offer, currency }: { offer: LandedOffer; currency: string }) {
  return (
    <>
      <Text size="sm" fw={600} c={crossingIsKnown(offer) ? undefined : 'orange.7'}>
        {formatMoney(offer.landedUnitCostBaseMinor, currency)}
      </Text>
      {!crossingIsKnown(offer) && (
        <Text size="xs" c="dimmed">
          Goods only — shipping not counted
        </Text>
      )}
    </>
  );
}

/**
 * What one vendor charges, said the way the rest of the app says it.
 *
 * The figure on the row is the price of ONE counted unit — a kilo of limes, a bottle of rum —
 * because that is the only figure two vendors selling different case sizes can be compared on, and
 * it is what "costs you" is worked out from. It used to be captioned "a case of 10", which reads as
 * the price OF the case: limes quoted at L 450.00 for ten kilos appeared on this screen as
 * "L 45.00 · a case of 10", and a case of twelve bottles of Flor de Caña at $144.00 appeared as
 * "$ 12.00 · a case of 12". Both understated the case by the size of the case, on the two screens
 * whose whole job is choosing who to buy from.
 *
 * The counted unit comes from the stock item rather than from `packUnit`: a vendor's catalogue line
 * may be written in whatever their price list says (one here is quoted in mL), while the bar counts
 * bottles, and a price is only comparable in the unit the shelf figure is kept in.
 */
export function TheirPrice({ offer, unit }: { offer: LandedOffer; unit?: string }) {
  const packed = offer.packQty > 1;
  return (
    <>
      <Text size="sm" component="span">
        {formatMoney(offer.unitPriceMinor, offer.currency)}
        {unit ? ` a ${unit}` : ' each'}
      </Text>
      {packed && (
        <Text size="xs" c="dimmed">
          {formatMoney(offer.packPriceMinor, offer.currency)} for {formatQty(offer.packQty, unit)}
        </Text>
      )}
    </>
  );
}

/**
 * Who sells this, cheapest once the crossing is paid.
 *
 * The ranking behind this has existed on the API since it was written, pinned to the owner's own
 * worked example in a test, and appeared on no screen — so the question it answers ("is the
 * mainland grower actually cheaper?") was still being done in somebody's head, one vendor page at
 * a time. The two columns that matter are next to each other on purpose: their price, and their
 * price after the ferry. A vendor being dearer per kilo and cheaper landed is the whole point.
 */
export function WhoSellsIt({
  offers,
  currency,
  unit,
  emptyNote,
}: {
  offers: LandedOffer[];
  /** The money the books are kept in — landed cost is worked out in it. */
  currency: string;
  /** The unit this item is counted in, so a price can say what it is a price for. */
  unit?: string;
  emptyNote: string;
}) {
  if (offers.length === 0) return <EmptyNote>{emptyNote}</EmptyNote>;
  const cheapest = offers[0]!.landedUnitCostBaseMinor;

  return (
    <>
      <Table.ScrollContainer minWidth={720}>
        <Table verticalSpacing="sm" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Vendor</Table.Th>
              <Table.Th>Their price</Table.Th>
              <Table.Th>Shipping</Table.Th>
              <Table.Th>Costs you</Table.Th>
              <Table.Th>Last bought</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {offers.map((offer) => (
              <Table.Tr key={offer.supplierId}>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Anchor component={Link} to={`/suppliers/${offer.supplierId}`} fw={600} size="sm">
                      {offer.supplierName}
                    </Anchor>
                    {/* The badge is a recommendation, so it may only be as strong as what is behind
                        it. On a row whose shipping nobody has recorded, "Cheapest" would be a claim
                        about a number the row itself says is missing. */}
                    {offer.landedUnitCostBaseMinor === cheapest &&
                      offers.length > 1 &&
                      (crossingIsKnown(offer) ? (
                        <Badge size="sm" variant="light" color="green">
                          Cheapest
                        </Badge>
                      ) : (
                        <Badge size="sm" variant="light" color="orange">
                          Cheapest on goods alone
                        </Badge>
                      ))}
                  </Group>
                  {typeof offer.leadTimeDays === 'number' && (
                    <Text size="xs" c="dimmed">
                      About {offer.leadTimeDays} {offer.leadTimeDays === 1 ? 'day' : 'days'} to arrive
                    </Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <TheirPrice offer={offer} unit={unit} />
                </Table.Td>
                <Table.Td>
                  <Crossing offer={offer} currency={currency} unit={unit} />
                </Table.Td>
                <Table.Td>
                  <LandedCost offer={offer} currency={currency} />
                </Table.Td>
                <Table.Td>
                  {offer.lastPurchaseAt ? (
                    formatDate(offer.lastPurchaseAt)
                  ) : (
                    // A price nobody has ever bought against is a quote somebody typed in, and it
                    // sorts above a vendor the bar uses every week without saying so.
                    <Text size="sm" c="dimmed" fs="italic">
                      Never
                    </Text>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {/* Said once under the table rather than in a tooltip on every row: the reader is about to
          choose a vendor on these figures and is entitled to know they come from bills that were
          actually paid, and from which ones. */}
      <Text size="xs" c="dimmed" mt="xs">
        Shipping is worked out from what the ferry and the freight forwarders billed for each vendor's own
        deliveries — the mainland vendors do not charge for it, because they do not ship anything. A vendor
        nobody has recorded a shipping bill for is ranked on their goods price alone.
      </Text>
    </>
  );
}
